import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Monitor,
  MonitorOff,
  Users,
  MessageSquare,
  Hand,
  Settings,
  PhoneOff,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Copy,
  Clock,
  Calendar,
  Plus,
  Edit3,
  Trash2,
  Play,
  Square,
  MoreVertical,
  Download,
  Upload,
  FileText,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  LiveSession,
  LiveSessionParticipant,
  LiveSessionStatus,
} from "../../types/elearning";
import apiClient from "../../services/api";
import { io, Socket } from "socket.io-client";

interface LiveSessionState {
  session: LiveSession | null;
  participants: LiveSessionParticipant[];
  isJoined: boolean;
  localStream: MediaStream | null;
  remoteStreams: { [userId: string]: MediaStream };
  connectionStatus: "connecting" | "connected" | "disconnected" | "error";
  chatMessages: ChatMessage[];
  chatInput: string;
  showChat: boolean;
  showParticipants: boolean;
  raisedHands: string[];
  isRecording: boolean;
  recordingUrl: string | null;
  screenSharing: boolean;
  audioEnabled: boolean;
  videoEnabled: boolean;
  isFullscreen: boolean;
  volume: number;
  loading: boolean;
  error: string | null;
}

interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: Date;
  type: "text" | "system" | "file";
}

interface MediaControlsProps {
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  isRecording: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onToggleRecording: () => void;
  onEndSession: () => void;
  isHost: boolean;
}

const MediaControls: React.FC<MediaControlsProps> = ({
  audioEnabled,
  videoEnabled,
  screenSharing,
  isRecording,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onToggleRecording,
  onEndSession,
  isHost,
}) => {
  return (
    <div className="flex items-center justify-center space-x-4 bg-gray-900 p-4 rounded-lg">
      <button
        onClick={onToggleAudio}
        className={`p-3 rounded-full ${
          audioEnabled
            ? "bg-gray-700 hover:bg-gray-600"
            : "bg-red-600 hover:bg-red-700"
        } text-white transition-colors`}
      >
        {audioEnabled ? (
          <Mic className="w-5 h-5" />
        ) : (
          <MicOff className="w-5 h-5" />
        )}
      </button>

      <button
        onClick={onToggleVideo}
        className={`p-3 rounded-full ${
          videoEnabled
            ? "bg-gray-700 hover:bg-gray-600"
            : "bg-red-600 hover:bg-red-700"
        } text-white transition-colors`}
      >
        {videoEnabled ? (
          <Video className="w-5 h-5" />
        ) : (
          <VideoOff className="w-5 h-5" />
        )}
      </button>

      <button
        onClick={onToggleScreenShare}
        className={`p-3 rounded-full ${
          screenSharing
            ? "bg-blue-600 hover:bg-blue-700"
            : "bg-gray-700 hover:bg-gray-600"
        } text-white transition-colors`}
      >
        {screenSharing ? (
          <MonitorOff className="w-5 h-5" />
        ) : (
          <Monitor className="w-5 h-5" />
        )}
      </button>

      {isHost && (
        <button
          onClick={onToggleRecording}
          className={`p-3 rounded-full ${
            isRecording
              ? "bg-red-600 hover:bg-red-700"
              : "bg-gray-700 hover:bg-gray-600"
          } text-white transition-colors`}
        >
          {isRecording ? (
            <Square className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5" />
          )}
        </button>
      )}

      <button
        onClick={onEndSession}
        className="p-3 rounded-full bg-red-600 hover:bg-red-700 text-white transition-colors"
      >
        <PhoneOff className="w-5 h-5" />
      </button>
    </div>
  );
};

interface ParticipantListProps {
  participants: LiveSessionParticipant[];
  raisedHands: string[];
  currentUserId: string;
  isHost: boolean;
  onMuteParticipant: (userId: string) => void;
  onRemoveParticipant: (userId: string) => void;
  onPromoteToHost: (userId: string) => void;
}

const ParticipantList: React.FC<ParticipantListProps> = ({
  participants,
  raisedHands,
  currentUserId,
  isHost,
  onMuteParticipant,
  onRemoveParticipant,
  onPromoteToHost,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
        <Users className="w-4 h-4 mr-2" />
        Participants ({participants.length})
      </h3>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {participants.map((participant) => (
          <div
            key={participant.userId}
            className="flex items-center justify-between p-2 rounded-lg bg-gray-50"
          >
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-800">
                    {participant.name.charAt(0).toUpperCase()}
                  </span>
                </div>

                {raisedHands.includes(participant.userId) && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                    <Hand className="w-2 h-2 text-white" />
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    {participant.name}
                  </span>
                  {participant.role === "host" && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      Host
                    </span>
                  )}
                  {participant.userId === currentUserId && (
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                      You
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  {!participant.audioEnabled && <MicOff className="w-3 h-3" />}
                  {!participant.videoEnabled && (
                    <VideoOff className="w-3 h-3" />
                  )}
                  <span
                    className={`w-2 h-2 rounded-full ${
                      participant.connectionStatus === "connected"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  />
                </div>
              </div>
            </div>

            {isHost && participant.userId !== currentUserId && (
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => onMuteParticipant(participant.userId)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Mute participant"
                >
                  <MicOff className="w-4 h-4" />
                </button>

                <button
                  onClick={() => onPromoteToHost(participant.userId)}
                  className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  title="Make host"
                >
                  <Settings className="w-4 h-4" />
                </button>

                <button
                  onClick={() => onRemoveParticipant(participant.userId)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                  title="Remove participant"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface ChatPanelProps {
  messages: ChatMessage[];
  chatInput: string;
  onChatInputChange: (value: string) => void;
  onSendMessage: () => void;
  onSendFile: (file: File) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  messages,
  chatInput,
  onChatInputChange,
  onSendMessage,
  onSendFile,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onSendFile(file);
      e.target.value = "";
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col h-96">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
        <MessageSquare className="w-4 h-4 mr-2" />
        Chat
      </h3>

      <div className="flex-1 overflow-y-auto space-y-2 mb-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`text-sm ${
              message.type === "system"
                ? "text-center text-gray-500 italic"
                : ""
            }`}
          >
            {message.type === "system" ? (
              <span>{message.message}</span>
            ) : (
              <div>
                <div className="flex items-baseline space-x-2 mb-1">
                  <span className="font-medium text-gray-900">
                    {message.userName}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatTime(message.timestamp)}
                  </span>
                </div>

                {message.type === "file" ? (
                  <div className="flex items-center space-x-2 p-2 bg-blue-50 rounded text-blue-800">
                    <FileText className="w-4 h-4" />
                    <span>{message.message}</span>
                    <button className="text-blue-600 hover:text-blue-800">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-gray-800">{message.message}</p>
                )}
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="flex items-end space-x-2">
        <div className="flex-1">
          <textarea
            value={chatInput}
            onChange={(e) => onChatInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            rows={2}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <div className="flex flex-col space-y-1">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-gray-400 hover:text-blue-600 rounded"
            title="Send file"
          >
            <Upload className="w-4 h-4" />
          </button>

          <button
            onClick={onSendMessage}
            disabled={!chatInput.trim()}
            className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileUpload}
        className="hidden"
        accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
      />
    </div>
  );
};

const LiveSessionRoom: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [socket, setSocket] = useState<Socket | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideosRef = useRef<{ [userId: string]: HTMLVideoElement }>({});
  const peerConnections = useRef<{ [userId: string]: RTCPeerConnection }>({});

  const [state, setState] = useState<LiveSessionState>({
    session: null,
    participants: [],
    isJoined: false,
    localStream: null,
    remoteStreams: {},
    connectionStatus: "disconnected",
    chatMessages: [],
    chatInput: "",
    showChat: true,
    showParticipants: true,
    raisedHands: [],
    isRecording: false,
    recordingUrl: null,
    screenSharing: false,
    audioEnabled: true,
    videoEnabled: true,
    isFullscreen: false,
    volume: 100,
    loading: true,
    error: null,
  });

  // Mock current user data
  const currentUserId = "user123";
  const currentUserName = "John Doe";
  const isHost = true;

  useEffect(() => {
    if (sessionId) {
      loadSession();
      initializeSocket();
    }

    return () => {
      cleanup();
    };
  }, [sessionId]);

  const loadSession = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const response = await apiClient.get(
        `/elearning/live-sessions/${sessionId}`
      );
      const session = response.data;

      setState((prev) => ({
        ...prev,
        session,
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error ? error.message : "Failed to load session",
        loading: false,
      }));
    }
  };

  const initializeSocket = () => {
    const newSocket = io(
      import.meta.env.VITE_BACKEND_URL || "http://localhost:5000"
    );

    newSocket.on("connect", () => {
      console.log("Connected to live session socket");
      setState((prev) => ({ ...prev, connectionStatus: "connected" }));
    });

    newSocket.on("disconnect", () => {
      setState((prev) => ({ ...prev, connectionStatus: "disconnected" }));
    });

    newSocket.on("user-joined", (participant: LiveSessionParticipant) => {
      setState((prev) => ({
        ...prev,
        participants: [...prev.participants, participant],
        chatMessages: [
          ...prev.chatMessages,
          {
            id: Date.now().toString(),
            userId: "system",
            userName: "System",
            message: `${participant.name} joined the session`,
            timestamp: new Date(),
            type: "system",
          },
        ],
      }));
    });

    newSocket.on("user-left", (userId: string) => {
      setState((prev) => ({
        ...prev,
        participants: prev.participants.filter((p) => p.userId !== userId),
        raisedHands: prev.raisedHands.filter((id) => id !== userId),
      }));
    });

    newSocket.on("chat-message", (message: ChatMessage) => {
      setState((prev) => ({
        ...prev,
        chatMessages: [...prev.chatMessages, message],
      }));
    });

    newSocket.on("hand-raised", (userId: string) => {
      setState((prev) => ({
        ...prev,
        raisedHands: [...prev.raisedHands, userId],
      }));
    });

    newSocket.on("hand-lowered", (userId: string) => {
      setState((prev) => ({
        ...prev,
        raisedHands: prev.raisedHands.filter((id) => id !== userId),
      }));
    });

    newSocket.on("recording-started", () => {
      setState((prev) => ({ ...prev, isRecording: true }));
    });

    newSocket.on("recording-stopped", (recordingUrl: string) => {
      setState((prev) => ({ ...prev, isRecording: false, recordingUrl }));
    });

    setSocket(newSocket);
  };

  const joinSession = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: state.videoEnabled,
        audio: state.audioEnabled,
      });

      setState((prev) => ({ ...prev, localStream: stream, isJoined: true }));

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      if (socket) {
        socket.emit("join-session", {
          sessionId,
          userId: currentUserId,
          userName: currentUserName,
        });
      }
    } catch (error) {
      console.error("Error joining session:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to access camera/microphone",
      }));
    }
  };

  const leaveSession = () => {
    cleanup();
    navigate("/elearning/sessions");
  };

  const cleanup = () => {
    if (state.localStream) {
      state.localStream.getTracks().forEach((track) => track.stop());
    }

    Object.values(peerConnections.current).forEach((pc) => {
      pc.close();
    });

    if (socket) {
      socket.disconnect();
    }
  };

  const toggleAudio = () => {
    if (state.localStream) {
      const audioTrack = state.localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !state.audioEnabled;
        setState((prev) => ({ ...prev, audioEnabled: !prev.audioEnabled }));
      }
    }
  };

  const toggleVideo = () => {
    if (state.localStream) {
      const videoTrack = state.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !state.videoEnabled;
        setState((prev) => ({ ...prev, videoEnabled: !prev.videoEnabled }));
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (!state.screenSharing) {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        });

        setState((prev) => ({ ...prev, screenSharing: true }));

        // Replace video track with screen share
        if (state.localStream) {
          const videoTrack = screenStream.getVideoTracks()[0];
          const sender = Object.values(peerConnections.current)[0]
            ?.getSenders()
            .find((s) => s.track?.kind === "video");

          if (sender) {
            await sender.replaceTrack(videoTrack);
          }
        }

        screenStream.getVideoTracks()[0].onended = () => {
          setState((prev) => ({ ...prev, screenSharing: false }));
        };
      } else {
        // Stop screen sharing and return to camera
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: state.audioEnabled,
        });

        setState((prev) => ({
          ...prev,
          screenSharing: false,
          localStream: stream,
        }));
      }
    } catch (error) {
      console.error("Error toggling screen share:", error);
    }
  };

  const toggleRecording = () => {
    if (socket) {
      if (state.isRecording) {
        socket.emit("stop-recording", sessionId);
      } else {
        socket.emit("start-recording", sessionId);
      }
    }
  };

  const sendChatMessage = () => {
    if (!state.chatInput.trim() || !socket) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      userId: currentUserId,
      userName: currentUserName,
      message: state.chatInput,
      timestamp: new Date(),
      type: "text",
    };

    socket.emit("chat-message", { sessionId, message });
    setState((prev) => ({
      ...prev,
      chatInput: "",
      chatMessages: [...prev.chatMessages, message],
    }));
  };

  const sendFile = async (file: File) => {
    // In a real implementation, upload file to server and send download link
    const message: ChatMessage = {
      id: Date.now().toString(),
      userId: currentUserId,
      userName: currentUserName,
      message: file.name,
      timestamp: new Date(),
      type: "file",
    };

    if (socket) {
      socket.emit("chat-message", { sessionId, message });
    }
  };

  const raiseHand = () => {
    if (socket) {
      const isRaised = state.raisedHands.includes(currentUserId);

      if (isRaised) {
        socket.emit("lower-hand", { sessionId, userId: currentUserId });
      } else {
        socket.emit("raise-hand", { sessionId, userId: currentUserId });
      }
    }
  };

  const muteParticipant = (userId: string) => {
    if (socket && isHost) {
      socket.emit("mute-participant", { sessionId, userId });
    }
  };

  const removeParticipant = (userId: string) => {
    if (socket && isHost) {
      socket.emit("remove-participant", { sessionId, userId });
    }
  };

  const promoteToHost = (userId: string) => {
    if (socket && isHost) {
      socket.emit("promote-to-host", { sessionId, userId });
    }
  };

  const copySessionLink = () => {
    const link = `${window.location.origin}/elearning/live-sessions/${sessionId}`;
    navigator.clipboard.writeText(link);
    // Show toast notification
  };

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading session...</p>
        </div>
      </div>
    );
  }

  if (state.error || !state.session) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h2 className="text-xl font-semibold mb-2">Session Error</h2>
          <p className="mb-4">{state.error}</p>
          <button
            onClick={() => navigate("/elearning/sessions")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  if (!state.isJoined) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <Video className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {state.session.title}
            </h2>
            <p className="text-gray-600">{state.session.description}</p>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Camera</span>
              <button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    videoEnabled: !prev.videoEnabled,
                  }))
                }
                className={`p-2 rounded ${
                  state.videoEnabled
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {state.videoEnabled ? (
                  <Video className="w-4 h-4" />
                ) : (
                  <VideoOff className="w-4 h-4" />
                )}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Microphone</span>
              <button
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    audioEnabled: !prev.audioEnabled,
                  }))
                }
                className={`p-2 rounded ${
                  state.audioEnabled
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {state.audioEnabled ? (
                  <Mic className="w-4 h-4" />
                ) : (
                  <MicOff className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <button
            onClick={joinSession}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Join Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold">{state.session.title}</h1>

            <div className="flex items-center space-x-2 text-sm text-gray-300">
              <div
                className={`w-2 h-2 rounded-full ${
                  state.connectionStatus === "connected"
                    ? "bg-green-500"
                    : "bg-red-500"
                }`}
              />
              <span className="capitalize">{state.connectionStatus}</span>
            </div>

            {state.isRecording && (
              <div className="flex items-center space-x-2 text-red-400">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm">Recording</span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={copySessionLink}
              className="flex items-center space-x-2 px-3 py-1 bg-gray-700 rounded text-sm hover:bg-gray-600"
            >
              <Copy className="w-4 h-4" />
              <span>Copy Link</span>
            </button>

            <button
              onClick={raiseHand}
              className={`p-2 rounded ${
                state.raisedHands.includes(currentUserId)
                  ? "bg-yellow-600 hover:bg-yellow-700"
                  : "bg-gray-700 hover:bg-gray-600"
              }`}
            >
              <Hand className="w-4 h-4" />
            </button>

            <button
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  showParticipants: !prev.showParticipants,
                }))
              }
              className="p-2 bg-gray-700 rounded hover:bg-gray-600"
            >
              <Users className="w-4 h-4" />
            </button>

            <button
              onClick={() =>
                setState((prev) => ({ ...prev, showChat: !prev.showChat }))
              }
              className="p-2 bg-gray-700 rounded hover:bg-gray-600"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-screen">
        {/* Main Video Area */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
              {/* Local Video */}
              <div className="relative bg-gray-800 rounded-lg overflow-hidden">
                <video
                  ref={localVideoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-black/50 px-2 py-1 rounded text-sm">
                  {currentUserName} (You)
                </div>
                {!state.videoEnabled && (
                  <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                    <div className="text-center">
                      <VideoOff className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-400">Camera off</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Remote Videos */}
              {state.participants
                .filter((p) => p.userId !== currentUserId)
                .map((participant) => (
                  <div
                    key={participant.userId}
                    className="relative bg-gray-800 rounded-lg overflow-hidden"
                  >
                    <video
                      ref={(el) => {
                        if (el)
                          remoteVideosRef.current[participant.userId] = el;
                      }}
                      autoPlay
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 left-4 bg-black/50 px-2 py-1 rounded text-sm">
                      {participant.name}
                    </div>
                    {!participant.videoEnabled && (
                      <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-2">
                            <span className="text-2xl font-medium">
                              {participant.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <p className="text-gray-400">{participant.name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>

          {/* Media Controls */}
          <div className="p-4">
            <MediaControls
              audioEnabled={state.audioEnabled}
              videoEnabled={state.videoEnabled}
              screenSharing={state.screenSharing}
              isRecording={state.isRecording}
              onToggleAudio={toggleAudio}
              onToggleVideo={toggleVideo}
              onToggleScreenShare={toggleScreenShare}
              onToggleRecording={toggleRecording}
              onEndSession={leaveSession}
              isHost={isHost}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-gray-50 border-l border-gray-200">
          <div className="h-full flex flex-col">
            {/* Participants */}
            {state.showParticipants && (
              <div className="p-4 border-b border-gray-200">
                <ParticipantList
                  participants={state.participants}
                  raisedHands={state.raisedHands}
                  currentUserId={currentUserId}
                  isHost={isHost}
                  onMuteParticipant={muteParticipant}
                  onRemoveParticipant={removeParticipant}
                  onPromoteToHost={promoteToHost}
                />
              </div>
            )}

            {/* Chat */}
            {state.showChat && (
              <div className="flex-1 p-4">
                <ChatPanel
                  messages={state.chatMessages}
                  chatInput={state.chatInput}
                  onChatInputChange={(value) =>
                    setState((prev) => ({ ...prev, chatInput: value }))
                  }
                  onSendMessage={sendChatMessage}
                  onSendFile={sendFile}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveSessionRoom;
